import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2, ChevronRight, ChevronDown } from "lucide-react";
import { RequireAdmin } from "@/components/shared/require-admin";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/shared/delete-confirm-dialog";
import { FormError } from "@/components/shared/form-error";
import {
  useStrategicPlans,
  useCreateStrategicPlan,
  useUpdateStrategicPlan,
  useDeleteStrategicPlan,
  type StrategicPlan,
} from "@/hooks/use-strategic-plans";
import {
  useFocusAreas,
  useCreateFocusArea,
  useUpdateFocusArea,
  useDeleteFocusArea,
  type FocusArea,
} from "@/hooks/use-focus-areas";
import {
  useTactics,
  useCreateTactic,
  useUpdateTactic,
  useDeleteTactic,
  type Tactic,
} from "@/hooks/use-tactics";
import { useCollegeGoals } from "@/hooks/use-college-goals";
import { useCollegeStrategicPillars } from "@/hooks/use-college-strategic-pillars";
import { primaryOf } from "@/lib/alignment-helpers";

// ── Zod Schemas ──────────────────────────────────────────────

const strategicPlanFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  description: z.string().max(5000).optional(),
  time_frame: z.string().max(255).optional(),
  sort_order: z.number().int().min(0).optional(),
});
type StrategicPlanFormValues = z.infer<typeof strategicPlanFormSchema>;

const focusAreaFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  description: z.string().max(5000).optional(),
  coach_context: z.string().max(500).optional(),
  fa_narrative: z.string().max(10000).optional(),
  sort_order: z.number().int().min(0).optional(),
  college_goal_ids: z.array(z.string()).optional(),
});
type FocusAreaFormValues = z.infer<typeof focusAreaFormSchema>;

const tacticFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(500),
  reference_code: z.string().max(50).optional(),
  description: z.string().max(5000).optional(),
  measurables: z.string().max(5000).optional(),
  coach_context: z.string().max(500).optional(),
  sort_order: z.number().int().min(0).optional(),
});
type TacticFormValues = z.infer<typeof tacticFormSchema>;

// ── Skeleton ─────────────────────────────────────────────────

function PlansTableSkeleton() {
  return (
    <div className="space-y-3" role="status" aria-label="Loading strategic plans">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Name</TableHead>
              <TableHead className="hidden sm:table-cell">Time Frame</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                <TableCell className="hidden sm:table-cell"><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-8 w-20" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Strategic Plan Form Dialog ───────────────────────────────

function StrategicPlanFormDialog({
  open,
  onOpenChange,
  plan,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: StrategicPlan | null;
}) {
  const isEditing = plan !== null;
  const createPlan = useCreateStrategicPlan();
  const updatePlan = useUpdateStrategicPlan(plan?.id ?? "");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StrategicPlanFormValues>({
    resolver: zodResolver(strategicPlanFormSchema),
    defaultValues: getStrategicPlanDefaults(plan),
  });

  useEffect(() => {
    reset(getStrategicPlanDefaults(plan));
  }, [plan, reset]);

  const isPending = createPlan.isPending || updatePlan.isPending;

  const onSubmit = (data: StrategicPlanFormValues) => {
    const body = {
      name: data.name,
      description: data.description || null,
      time_frame: data.time_frame || null,
      sort_order: data.sort_order ?? 0,
    };
    if (isEditing) {
      updatePlan.mutate(body, {
        onSuccess: () => { onOpenChange(false); reset(); },
      });
    } else {
      createPlan.mutate(body, {
        onSuccess: () => { onOpenChange(false); reset(); },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Strategic Plan" : "Create Strategic Plan"}</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update the strategic plan details." : "Add a new strategic plan."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="plan-name">Name</Label>
            <Input id="plan-name" placeholder='e.g., "ITS Strategic Focus Areas"' {...register("name")} autoFocus />
            <FormError message={errors.name?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plan-description">Description</Label>
            <Textarea id="plan-description" placeholder="Describe the plan..." rows={3} {...register("description")} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="plan-time-frame">Time Frame</Label>
              <Input id="plan-time-frame" placeholder='e.g., "2024 – 2027"' {...register("time_frame")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan-sort-order">Sort Order</Label>
              <Input id="plan-sort-order" type="number" {...register("sort_order", { valueAsNumber: true })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (isEditing ? "Saving..." : "Creating...") : isEditing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function getStrategicPlanDefaults(plan: StrategicPlan | null): StrategicPlanFormValues {
  return {
    name: plan?.name ?? "",
    description: plan?.description ?? "",
    time_frame: plan?.time_frame ?? "",
    sort_order: plan?.sort_order ?? 0,
  };
}

// DeleteStrategicPlanDialog replaced by DeleteConfirmDialog (inline at usage site)

// ── Focus Area Form Dialog ───────────────────────────────────

interface CollegeGoalOptionGroup {
  group: string;
  items: Array<{ value: string; label: string }>;
}

function useCollegeGoalOptionGroups(): CollegeGoalOptionGroup[] {
  const { data: goalsData } = useCollegeGoals();
  const { data: pillarsData } = useCollegeStrategicPillars();

  return useMemo(() => {
    const goals = goalsData?.data ?? [];
    const pillars = pillarsData?.data ?? [];
    const pillarMap = new Map(pillars.map((p) => [p.id, p.name]));
    const byPillar = new Map<string, Array<{ value: string; label: string }>>();
    for (const g of goals) {
      const pillarName = pillarMap.get(g.college_strategic_pillar_id) ?? "Other";
      if (!byPillar.has(pillarName)) byPillar.set(pillarName, []);
      byPillar.get(pillarName)!.push({ value: g.id, label: g.name });
    }
    return Array.from(byPillar.entries()).map(([group, items]) => ({ group, items }));
  }, [goalsData, pillarsData]);
}

function FocusAreaFormDialog({
  open,
  onOpenChange,
  focusArea,
  strategicPlanId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  focusArea: FocusArea | null;
  strategicPlanId: string;
}) {
  const isEditing = focusArea !== null;
  const createFocusArea = useCreateFocusArea();
  const updateFocusArea = useUpdateFocusArea(focusArea?.id ?? "");
  const collegeGoalGroups = useCollegeGoalOptionGroups();
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FocusAreaFormValues>({
    resolver: zodResolver(focusAreaFormSchema),
    defaultValues: getFocusAreaDefaults(focusArea),
  });

  useEffect(() => {
    reset(getFocusAreaDefaults(focusArea));
    setSelectedGoalId(primaryOf(focusArea?.college_goal_ids));
  }, [focusArea, reset]);

  const isPending = createFocusArea.isPending || updateFocusArea.isPending;

  const onSubmit = (data: FocusAreaFormValues) => {
    if (isEditing) {
      updateFocusArea.mutate(
        { name: data.name, description: data.description || null, coach_context: data.coach_context || null, fa_narrative: data.fa_narrative || null, sort_order: data.sort_order ?? 0, college_goal_ids: selectedGoalId ? [selectedGoalId] : [] },
        { onSuccess: () => { onOpenChange(false); reset(); setSelectedGoalId(null); } },
      );
    } else {
      createFocusArea.mutate(
        { strategic_plan_id: strategicPlanId, name: data.name, description: data.description || null, sort_order: data.sort_order ?? 0, college_goal_ids: selectedGoalId ? [selectedGoalId] : [] },
        { onSuccess: () => { onOpenChange(false); reset(); setSelectedGoalId(null); } },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit ITS Focus Area" : "Create ITS Focus Area"}</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update the focus area details." : "Add a new focus area to this plan."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fa-name">Name</Label>
            <Input id="fa-name" placeholder='e.g., "Fortify Defenses (Protect)"' {...register("name")} autoFocus />
            <FormError message={errors.name?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fa-description">Description</Label>
            <Textarea id="fa-description" placeholder="Describe the focus area..." rows={3} {...register("description")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fa-coach-context">AI Coach Summary <span className="text-xs text-muted-foreground font-normal">(2–3 sentences — shown inline in the strategy hierarchy)</span></Label>
            <Textarea id="fa-coach-context" placeholder="What is this Focus Area trying to accomplish and why does it matter?" rows={3} maxLength={500} {...register("coach_context")} />
            <FormError message={errors.coach_context?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fa-narrative">AI Coach Narrative <span className="text-xs text-muted-foreground font-normal">(2–4 paragraphs — injected when this FA is selected in the coach)</span></Label>
            <Textarea id="fa-narrative" placeholder="Describe the strategic context, the problem being solved, and what success looks like..." rows={6} maxLength={10000} {...register("fa_narrative")} />
            <FormError message={errors.fa_narrative?.message} />
          </div>
          <div className="space-y-2">
            <Label>College Objective</Label>
            <Select value={selectedGoalId ?? "__none__"} onValueChange={(v) => setSelectedGoalId(v === "__none__" ? null : v)}>
              <SelectTrigger>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {collegeGoalGroups.map((group) => (
                  <SelectGroup key={group.group}>
                    <SelectLabel>{group.group}</SelectLabel>
                    {group.items.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fa-sort-order">Sort Order</Label>
            <Input id="fa-sort-order" type="number" className="w-24" {...register("sort_order", { valueAsNumber: true })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (isEditing ? "Saving..." : "Creating...") : isEditing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function getFocusAreaDefaults(focusArea: FocusArea | null): FocusAreaFormValues {
  return {
    name: focusArea?.name ?? "",
    description: focusArea?.description ?? "",
    coach_context: focusArea?.coach_context ?? "",
    fa_narrative: focusArea?.fa_narrative ?? "",
    sort_order: focusArea?.sort_order ?? 0,
    college_goal_ids: focusArea?.college_goal_ids ?? [],
  };
}

// ── Delete Focus Area Dialog ─────────────────────────────────

// DeleteFocusAreaDialog replaced by DeleteConfirmDialog (inline at usage site)

// ── Tactic Form Dialog ───────────────────────────────────────

function TacticFormDialog({
  open,
  onOpenChange,
  tactic,
  focusAreaId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tactic: Tactic | null;
  focusAreaId: string;
}) {
  const isEditing = tactic !== null;
  const createTactic = useCreateTactic();
  const updateTactic = useUpdateTactic(tactic?.id ?? "");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TacticFormValues>({
    resolver: zodResolver(tacticFormSchema),
    defaultValues: getTacticDefaults(tactic),
  });

  useEffect(() => {
    reset(getTacticDefaults(tactic));
  }, [tactic, reset]);

  const isPending = createTactic.isPending || updateTactic.isPending;

  const onSubmit = (data: TacticFormValues) => {
    const base = {
      name: data.name,
      reference_code: data.reference_code || null,
      description: data.description || null,
      measurables: data.measurables || null,
      coach_context: data.coach_context || null,
      sort_order: data.sort_order ?? 0,
    };
    if (isEditing) {
      updateTactic.mutate(base, {
        onSuccess: () => { onOpenChange(false); reset(); },
      });
    } else {
      createTactic.mutate(
        { ...base, focus_area_id: focusAreaId },
        { onSuccess: () => { onOpenChange(false); reset(); } },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit ITS Strategic Initiative" : "Create ITS Strategic Initiative"}</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update the strategic initiative details." : "Add a new strategic initiative to this focus area."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[100px_1fr]">
            <div className="space-y-2">
              <Label htmlFor="tactic-code">Code</Label>
              <Input id="tactic-code" placeholder='e.g., "1.1"' {...register("reference_code")} autoFocus />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tactic-name">Name</Label>
              <Input id="tactic-name" placeholder="Strategic initiative title..." {...register("name")} />
              <FormError message={errors.name?.message} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tactic-description">Description</Label>
            <Textarea id="tactic-description" placeholder="Describe the strategic initiative..." rows={3} {...register("description")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tactic-measurables">Measurables</Label>
            <Textarea id="tactic-measurables" placeholder="Quantitative targets..." rows={2} {...register("measurables")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tactic-coach-context">AI Coach Summary <span className="text-xs text-muted-foreground font-normal">(2–3 sentences — shown inline in the strategy hierarchy)</span></Label>
            <Textarea id="tactic-coach-context" placeholder="What is this Strategic Initiative trying to accomplish?" rows={3} maxLength={500} {...register("coach_context")} />
            <FormError message={errors.coach_context?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tactic-sort-order">Sort Order</Label>
            <Input id="tactic-sort-order" type="number" className="w-24" {...register("sort_order", { valueAsNumber: true })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (isEditing ? "Saving..." : "Creating...") : isEditing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function getTacticDefaults(tactic: Tactic | null): TacticFormValues {
  return {
    name: tactic?.name ?? "",
    reference_code: tactic?.reference_code ?? "",
    description: tactic?.description ?? "",
    measurables: tactic?.measurables ?? "",
    coach_context: tactic?.coach_context ?? "",
    sort_order: tactic?.sort_order ?? 0,
  };
}

// ── Delete Tactic Dialog ─────────────────────────────────────

// DeleteTacticDialog replaced by DeleteConfirmDialog (inline at usage site)

// ── Tactics Sub-Table ────────────────────────────────────────

function TacticsSection({ focusAreaId }: { focusAreaId: string }) {
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedTactic, setSelectedTactic] = useState<Tactic | null>(null);
  const deleteTactic = useDeleteTactic();

  const { data, isLoading } = useTactics({ focus_area_id: focusAreaId });
  const tactics = data?.data ?? [];

  return (
    <div className="pl-12 pr-4 pb-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">ITS Strategic Initiatives</p>
        <Button size="sm" variant="outline" onClick={() => { setSelectedTactic(null); setFormOpen(true); }}>
          <Plus className="mr-1 h-3 w-3" /> New ITS Strategic Initiative
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
        </div>
      ) : tactics.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))] italic">No strategic initiatives yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tactics.map((tactic) => (
              <TableRow key={tactic.id}>
                <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">{tactic.reference_code ?? "—"}</TableCell>
                <TableCell className="text-sm">{tactic.name}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedTactic(tactic); setFormOpen(true); }} aria-label={`Edit strategic initiative ${tactic.name}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedTactic(tactic); setDeleteOpen(true); }} aria-label={`Delete strategic initiative ${tactic.name}`}>
                      <Trash2 className="h-3 w-3 text-[hsl(var(--destructive))]" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TacticFormDialog open={formOpen} onOpenChange={setFormOpen} tactic={selectedTactic} focusAreaId={focusAreaId} />
      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete ITS Strategic Initiative"
        description={<>Are you sure you want to delete <strong>{selectedTactic?.name}</strong>? This will fail if objectives reference it.</>}
        onConfirm={() => { if (selectedTactic) deleteTactic.mutate(selectedTactic.id, { onSuccess: () => setDeleteOpen(false) }); }}
        isPending={deleteTactic.isPending}
      />
    </div>
  );
}

// ── Focus Areas Sub-Table ────────────────────────────────────

function FocusAreasSection({ strategicPlanId }: { strategicPlanId: string }) {
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedFocusArea, setSelectedFocusArea] = useState<FocusArea | null>(null);
  const deleteFocusArea = useDeleteFocusArea();
  const [expandedFocusAreaIds, setExpandedFocusAreaIds] = useState<Set<string>>(new Set());

  const { data, isLoading } = useFocusAreas({ strategic_plan_id: strategicPlanId });
  const focusAreas = data?.data ?? [];

  const toggleFocusArea = (id: string) => {
    setExpandedFocusAreaIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="pl-8 pr-4 pb-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">ITS Focus Areas</p>
        <Button size="sm" variant="outline" onClick={() => { setSelectedFocusArea(null); setFormOpen(true); }}>
          <Plus className="mr-1 h-3 w-3" /> New ITS Focus Area
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
        </div>
      ) : focusAreas.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))] italic">No focus areas yet.</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Name</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {focusAreas.map((fa) => (
                <FocusAreaRow
                  key={fa.id}
                  focusArea={fa}
                  expanded={expandedFocusAreaIds.has(fa.id)}
                  onToggle={() => toggleFocusArea(fa.id)}
                  onEdit={() => { setSelectedFocusArea(fa); setFormOpen(true); }}
                  onDelete={() => { setSelectedFocusArea(fa); setDeleteOpen(true); }}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <FocusAreaFormDialog open={formOpen} onOpenChange={setFormOpen} focusArea={selectedFocusArea} strategicPlanId={strategicPlanId} />
      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete ITS Focus Area"
        description={<>Are you sure you want to delete <strong>{selectedFocusArea?.name}</strong>? This will fail if it has tactics or objectives referencing it.</>}
        onConfirm={() => { if (selectedFocusArea) deleteFocusArea.mutate(selectedFocusArea.id, { onSuccess: () => setDeleteOpen(false) }); }}
        isPending={deleteFocusArea.isPending}
      />
    </div>
  );
}

function FocusAreaRow({
  focusArea,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}: {
  focusArea: FocusArea;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      <TableRow>
        <TableCell>
          <Button variant="ghost" size="sm" onClick={onToggle} aria-label={expanded ? "Collapse tactics" : "Expand tactics"}>
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </TableCell>
        <TableCell className="font-medium">{focusArea.name}</TableCell>
        <TableCell>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit} aria-label={`Edit ${focusArea.name}`}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} aria-label={`Delete ${focusArea.name}`}>
              <Trash2 className="h-4 w-4 text-[hsl(var(--destructive))]" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={3} className="p-0 border-b">
            <TacticsSection focusAreaId={focusArea.id} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ── Main Page ────────────────────────────────────────────────

export function AdminStrategicPlansPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<StrategicPlan | null>(null);
  const [expandedPlanIds, setExpandedPlanIds] = useState<Set<string>>(new Set());
  const deletePlan = useDeleteStrategicPlan();

  const { data, isLoading, error } = useStrategicPlans();
  const plans = data?.data ?? [];

  const togglePlan = (id: string) => {
    setExpandedPlanIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <RequireAdmin>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="font-heading text-2xl font-semibold sm:text-3xl">ITS Strategic Focus</h1>
          <Button onClick={() => { setSelectedPlan(null); setFormOpen(true); }} aria-label="Create new strategic plan">
            <Plus className="mr-2 h-4 w-4" />
            New Strategic Plan
          </Button>
        </div>

        {isLoading ? (
          <PlansTableSkeleton />
        ) : error ? (
          <EmptyState title="Failed to load strategic plans" description={error.message} />
        ) : plans.length === 0 ? (
          <EmptyState
            title="No strategic plans found"
            description="Create your first strategic plan to get started."
            actionLabel="New Strategic Plan"
            onAction={() => { setSelectedPlan(null); setFormOpen(true); }}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden sm:table-cell">Time Frame</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => (
                  <PlanRow
                    key={plan.id}
                    plan={plan}
                    expanded={expandedPlanIds.has(plan.id)}
                    onToggle={() => togglePlan(plan.id)}
                    onEdit={() => { setSelectedPlan(plan); setFormOpen(true); }}
                    onDelete={() => { setSelectedPlan(plan); setDeleteOpen(true); }}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <StrategicPlanFormDialog open={formOpen} onOpenChange={setFormOpen} plan={selectedPlan} />
        <DeleteConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete Strategic Plan"
          description={<>Are you sure you want to delete <strong>{selectedPlan?.name}</strong>? This will fail if the plan still has focus areas.</>}
          onConfirm={() => { if (selectedPlan) deletePlan.mutate(selectedPlan.id, { onSuccess: () => setDeleteOpen(false) }); }}
          isPending={deletePlan.isPending}
        />
      </div>
    </RequireAdmin>
  );
}

function PlanRow({
  plan,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}: {
  plan: StrategicPlan;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      <TableRow>
        <TableCell>
          <Button variant="ghost" size="sm" onClick={onToggle} aria-label={expanded ? "Collapse focus areas" : "Expand focus areas"}>
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </TableCell>
        <TableCell className="font-medium">{plan.name}</TableCell>
        <TableCell className="hidden sm:table-cell text-[hsl(var(--muted-foreground))]">
          {plan.time_frame ?? "—"}
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit} aria-label={`Edit ${plan.name}`}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} aria-label={`Delete ${plan.name}`}>
              <Trash2 className="h-4 w-4 text-[hsl(var(--destructive))]" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={4} className="p-0 border-b">
            <FocusAreasSection strategicPlanId={plan.id} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
